import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { url } from 'projects/shared/src/lib/helpers/url';
import { SharedSettings } from 'projects/shared/src/lib/models/shared-settings';
import { ActivityBlockingService } from 'projects/shared/src/lib/services/activity-blocking.service';
import {BehaviorSubject, catchError, Observable, of, Subscription} from 'rxjs';
import { ShiftContextService } from './shift-context.service';
import { InvCountPlantInfo } from '../models/inv-count-plant-info';
import { InvCountProductCount } from '../models/inv-count-product-count';
import { InvCountProductHandling } from '../models/inv-count-product-handling';
import { InvCountTeardown } from '../models/inv-count-teardown';
import { InvCountTpmInfo } from '../models/inv-count-tpm-info';
import { InvCount } from '../models/inv-count';
import { SiteSubinventory } from 'projects/shared/src/lib/models/subinventory';
import { filter, map, tap } from 'rxjs/operators';
import { InvCountUacInfo } from '../models/inv-count-uac-info';
import { InvCountIgpsInfo } from '../models/inv-count-igps-info';

@Injectable({
    providedIn: 'root',
})
export class InventoryService {
    private _currentInvCount = new BehaviorSubject<InvCount | null>(null);

    get CurrentSubinventoryId(): Observable<number | null> {
        return this._currentInvCount.pipe(
            filter(inv => inv !== null),
            map(inv => inv?.siteSubinventoryId ?? null));
    }

    get CurrentInvProductCount(): Observable<InvCountProductCount[] | null> {
        return this._currentInvCount.pipe(
            map(inv => inv?.invCountProductCount ?? null));
    }
    get CurrentInvTeardowns(): Observable<InvCountTeardown[] | null> {
        return this._currentInvCount.pipe(
            map(inv => inv?.invCountTeardowns ?? null));
    }
    get CurrentInvProductHandling(): Observable<InvCountProductHandling[] | null> {
        return this._currentInvCount.pipe(
            map(inv => inv?.invCountProductHandling ?? null));
    }
    get CurrentPlantInfo(): Observable<InvCountPlantInfo | null> {
        return this._currentInvCount.pipe(
            map(inv => inv?.invCountPlantInfo ?? null));
    }
    get CurrentInvTpmInfo(): Observable<InvCountTpmInfo | null> {
        return this._currentInvCount.pipe(
            map(inv => inv?.invCountTpmInfo ?? null));
    }
    get CurrentUacInfo(): Observable<InvCountUacInfo | null> {
        return this._currentInvCount.pipe(
            map(inv => inv?.invCountUacInfo ?? null));
    }
    get CurrentIgpsInfo(): Observable<InvCountIgpsInfo | null> {
        return this._currentInvCount.pipe(
            map(inv => inv?.invCountIgpsInfo ?? null));
    }

    private subscriptions: Subscription[];
    private shiftContextId: number;

    constructor(
        private http: HttpClient,
        private settings: SharedSettings,
        private activityService: ActivityBlockingService,
        private shiftContextService: ShiftContextService
    ) {
        this.subscriptions = [
            this.shiftContextService.CurrentContext
            .subscribe(context => {
                this.shiftContextId = context.id;

                this._currentInvCount.next(null);
            }),
            this._currentInvCount.subscribe()
       ];
    }

    unload(): void {
        this._currentInvCount.next(null);
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    updateActiveSection(subinventory: SiteSubinventory): Observable<void> {
        if (subinventory.id !== this._currentInvCount.value?.siteSubinventoryId)
        this._currentInvCount.next(null);

        return this.http
            .get<InvCount>(this.settings.apiBaseUrl + url`Inventory?sitesubinventoryid=${subinventory.id}&workedshiftid=${this.shiftContextId}`)
            .pipe(
                this.activityService.Wait('Inventory Service getting InvCount'),
                map(currentInvCount => {
                    this._currentInvCount.next(currentInvCount);
                }),
                catchError(_err => {
                    this._currentInvCount.next(null);
                    return of(null);
                }));
    }

    updateInvCountPlantInfo(entry: InvCountPlantInfo): Observable<InvCountPlantInfo> {
        return this.http.put<InvCountPlantInfo>(this.settings.apiBaseUrl + url`Inventory/plantInfo`, entry);
    }

    updateInvCountTpmInfo(entry: InvCountTpmInfo): Observable<InvCountTpmInfo> {
        return this.http.put<InvCountTpmInfo>(this.settings.apiBaseUrl + url`Inventory/tpmInfo`, entry);
    }

    updateInvCountUacInfo(entry: InvCountUacInfo): Observable<InvCountUacInfo> {
        return this.http.put<InvCountUacInfo>(this.settings.apiBaseUrl + url`Inventory/uacInfo`, entry);
    }

    updateInvCountIgpsInfo(entry: InvCountIgpsInfo): Observable<InvCountIgpsInfo> {
        return this.http.put<InvCountIgpsInfo>(this.settings.apiBaseUrl + url`Inventory/igpsInfo`, entry);
    }

    createInvCountProductCount(entry: InvCountProductCount): Observable<InvCountProductCount> {
        return this.http.post<InvCountProductCount>(this.settings.apiBaseUrl + url`Inventory/productCount`, entry);
    }

    updateInvCountProductCount(entry: InvCountProductCount): Observable<InvCountProductCount> {
        return this.http.put<InvCountProductCount>(this.settings.apiBaseUrl + url`Inventory/productCount`, entry);
    }

    deleteInvCountProductCount(entryId: number): Observable<void> {
        return this.http.delete<void>(this.settings.apiBaseUrl + url`Inventory/productCount/${entryId}`);
    }

    createInvCountProductHandling(entry: InvCountProductHandling): Observable<InvCountProductHandling> {
        return this.http.post<InvCountProductHandling>(this.settings.apiBaseUrl + url`Inventory/productHandling`, entry);
    }

    updateInvCountProductHandling(entry: InvCountProductHandling): Observable<InvCountProductHandling> {
        return this.http.put<InvCountProductHandling>(this.settings.apiBaseUrl + url`Inventory/productHandling`, entry);
    }

    deleteInvCountProductHandling(handlingId: number): Observable<void> {
        return this.http.delete<void>(this.settings.apiBaseUrl + url`Inventory/productHandling/${handlingId}`);
    }

    createInvCountTeardownCount(entry: InvCountTeardown): Observable<InvCountTeardown> {
        return this.http.post<InvCountTeardown>(this.settings.apiBaseUrl + url`Inventory/teardown`, entry);
    }

    updateInvCountTeardown(entry: InvCountTeardown): Observable<InvCountTeardown> {
        return this.http.put<InvCountTeardown>(this.settings.apiBaseUrl + url`Inventory/teardown`, entry);
    }

    deleteInvCountTeardown(entryId: number): Observable<void> {
        return this.http.delete<void>(this.settings.apiBaseUrl + url`Inventory/teardown/${entryId}`);
    }

    copyPreviousCount(subInventoryId: number, currentCountId: number): void {
        this.http.post<InvCount>(this.settings.apiBaseUrl
                + url`Inventory/copyPreviousCount?sitesubinventoryid=${
                    subInventoryId
                }&workedshiftid=${
                    this.shiftContextId
                }&currentInvCountId=${
                    currentCountId
                }`, {})
            .pipe(this.activityService.Wait('Inventory Service getting Previous Count'))
            .subscribe({
                next: copiedInvCount => {
                    this._currentInvCount.next(copiedInvCount);
                }
            });
    }

    get CurrentInvCountContext(): Observable<InvCount> {
        return this._currentInvCount.asObservable();
    }
}
